import { useEffect, useState, useCallback } from "react";
import Iframe from "../../../components/Iframe";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  handleInputChangeWithScroll,
  MESSAGE_TYPES,
} from "../../../utils/editUtils";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  executeValidationWithArrays,
  handleInputChangeWithValidation,
  handleResetWithValidation,
} from "../../../utils/formValidation";

// パンくず
const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "About", path: `/about` },
];

function AboutEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();

  const contentKey = "aboutContent"; // ページのキー（aboutContentに変更）
  const [message, setMessage] = useState(""); // メッセージ
  const [loadingText, setLoadingText] = useState("");

  const [pageContent, setPageContent] = useState({}); // ページの表示データ
  // 複数動画リスト（ランダム表示用）
  const [pcVideos, setPcVideos] = useState([]);
  const [mobileVideos, setMobileVideos] = useState([]);
  const [conceptDescription, setConceptDescription] = useState([]);

  // 削除対象のIDを管理
  const [deletedIds, setDeletedIds] = useState([]);
  // 変更があったかどうかを追跡
  const [hasChanges, setHasChanges] = useState(false);

  // messageが文字列の場合、3秒後に自動で消去
  useEffect(() => {
    if (message && message.trim() !== "") {
      const timer = setTimeout(() => {
        setMessage("");
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [message]);

  // ファイルアップロードハンドラー
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }

      setLoadingText("アップロード中");

      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];

      if (!file) {
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        // アップロード完了後に選択状態にする
        if (data.images?.[0]) {
          // APIレスポンスをMediaGallery形式に変換
          const uploadedImage = {
            id: data.images[0].id,
            image_url: data.images[0].url || data.images[0].image_url,
            mime_type: data.images[0].mime_type,
            width: data.images[0].width,
            height: data.images[0].height,
            file_size: data.images[0].file_size,
          };

          // メディアファイル一覧を再取得
          await fetchMediaFiles();

          // fetchMediaFiles完了後に選択状態にする
          selectMedia(uploadedImage, true);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia, setLoadingText]
  );

  // アバウトの詳細取得
  const fetchPageContentData = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_about_content/`, { credentials: "include" });
      if (!response.ok) {
        throw new Error("ネットワークレスポンスが異常です。");
      }
      const data = await response.json();
      setPageContent(data);
      setPcVideos(data.pc_videos || []);
      setMobileVideos(data.mobile_videos || []);
      setConceptDescription(data.concept_description || []);
      setHasChanges(false);
      setDeletedIds([]);
    } catch (error) {
      console.error("データの取得中にエラーが発生しました:", error);
    }
    setLoadingText("");
  }, []);

  useEffect(() => {
    fetchPageContentData(); // アバウト詳細の取得
    fetchMediaFiles(); // 画像動画リストの取得
  }, [fetchPageContentData, fetchMediaFiles]);

  // アバウト詳細を更新
  const handlePageDataSubmit = async () => {
    // 更新するデータ
    const formData = {
      page_content: pageContent,
      pc_videos: pcVideos.map((v) => ({ id: v.id })),
      mobile_videos: mobileVideos.map((v) => ({ id: v.id })),
      concept_description: conceptDescription,
      deleted_concept_ids: deletedIds, // 削除されたIDを送信
    };

    // バリデーション実行（通常フィールドと配列フィールドを組み合わせた全データを対象とする）
    const allFormData = {
      ...pageContent,
      concept_description: conceptDescription,
    };

    if (
      !executeValidationWithArrays(
        allFormData,
        validationRules,
        arrayValidationRules,
        fieldLabels,
        setMessage
      )
    ) {
      return;
    }

    setLoadingText("保存中");

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/update_page_about_content/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(`${data.message} status: ${response.status}`);
      }
      setMessage(data.message);
      setHasChanges(false); // 変更フラグをリセット
      setDeletedIds([]); // 削除IDをリセット
    } catch (error) {
      console.error("There was a problem with the fetch operation:", error);
    }
    setLoadingText("");
  };

  // バリデーションルールを定義
  const validationRules = {
    concept_message: { required: true },
  };

  // 配列要素のバリデーションルール
  const arrayValidationRules = {
    concept_description: {
      title: { required: true },
      content: { required: true },
    },
  };

  // フィールドラベルのマッピング
  const fieldLabels = {
    concept_message: "コンセプトメッセージ",
  };

  // テキスト入力ハンドラー
  const handleInputChange = (e) => {
    handleInputChangeWithValidation(
      e,
      setPageContent,
      handleInputChangeWithScroll,
      {
        contentKey,
        currentData: pageContent,
        setData: setPageContent,
      }
    );
    setHasChanges(true);
  };

  // 複数動画リスト更新ハンドラー（pc_videos / mobile_videos）
  const handleVideosChange = useCallback(
    (fieldName, setter) => (videos) => {
      const normalized = Array.isArray(videos) ? videos : [];
      setter(normalized);
      setHasChanges(true);

      // iframeに通知
      setTimeout(() => {
        const iframe = document.querySelector("iframe");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
              contentKey,
              fieldName,
              value: normalized,
              target: `.edit_${fieldName}`,
            },
            process.env.REACT_APP_ORIGIN
          );
        }
      }, 100);
    },
    [contentKey]
  );

  // リセット処理
  const handleReset = () => {
    handleResetWithValidation(fetchPageContentData);
  };

  // コンセプト説明の追加
  const addConceptDescription = () => {
    setConceptDescription([
      ...conceptDescription,
      { title: "", content: "", image: null },
    ]);
    setHasChanges(true);
  };

  // 削除ボタンのハンドラー
  const handleDelete = (index, concept) => {
    // 削除対象のIDを追加（APIで使用）
    if (concept.id) {
      setDeletedIds([...deletedIds, concept.id]);
    }

    // 配列から削除
    const newConceptDescription = [...conceptDescription];
    newConceptDescription.splice(index, 1);
    if (newConceptDescription.length === 0)
      newConceptDescription.push({ title: "", content: "", image: null });
    setConceptDescription(newConceptDescription);
    setHasChanges(true);

    // iframeに通知
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.DELETE_CONCEPT,
          contentKey,
          conceptId: concept.id,
          conceptDescription: newConceptDescription,
        },
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // 追加ボタンのハンドラー
  const handleAdd = (index) => {
    const newConceptDescription = [...conceptDescription];
    // 新しい空のコンセプト説明を追加
    newConceptDescription.splice(index + 1, 0, {
      title: "",
      content: "",
      image: null,
    });
    setConceptDescription(newConceptDescription);
    setHasChanges(true);

    // iframeに通知
    const iframe = document.querySelector("iframe");
    if (iframe && iframe.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          type: MESSAGE_TYPES.ADD_CONCEPT,
          contentKey,
          index: index + 1,
          concept: newConceptDescription[index + 1],
          conceptDescription: newConceptDescription,
        },
        process.env.REACT_APP_ORIGIN
      );
    }
  };

  // コンセプト説明のテキスト更新ハンドラー
  const handleConceptInputChange = (e, index) => {
    const { name, value } = e.target;

    // name属性からフィールド名を抽出（concept_description0_title -> title）
    const fieldName = name.split("_").pop();

    // 通常の配列更新処理
    const newConceptDescription = [...conceptDescription];
    if (!newConceptDescription[index]) {
      newConceptDescription[index] = {};
    }
    newConceptDescription[index][fieldName] = value;
    setConceptDescription(newConceptDescription);
    setHasChanges(true);

    // エラークリア処理
    if (
      e.target &&
      e.target.classList &&
      e.target.classList.contains("validation-error")
    ) {
      e.target.classList.remove("validation-error");

      const wrapper = e.target.closest(".form-field-wrapper");
      if (wrapper) {
        const errorLabel = wrapper.querySelector(".validation-error-label");
        if (errorLabel) {
          errorLabel.remove();
        }
      }
    }

    // iframe通信処理
    setTimeout(() => {
      const iframe = document.querySelector("iframe");
      if (iframe && iframe.contentWindow) {
        // 特定の要素のセレクタ
        const targetSelector = `.edit_concept_description_${index}_${fieldName}`;

        // TEXT_UPDATEタイプのメッセージを送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: { concept_description: newConceptDescription },
          },
          process.env.REACT_APP_ORIGIN
        );

        // スクロール命令も送信
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.SCROLL_TO,
            target: targetSelector,
            contentKey,
          },
          process.env.REACT_APP_ORIGIN
        );
      }
    }, 100);
  };

  // コンセプト説明の画像更新ハンドラー
  const handleConceptImageChange = useCallback(
    (index, image) => {
      // 通常の配列更新処理
      setConceptDescription((prevConceptDescription) => {
        const newConceptDescription = [...prevConceptDescription];
        if (!newConceptDescription[index]) {
          newConceptDescription[index] = {};
        }
        newConceptDescription[index].image = image;

        // iframe通信処理（Reactのステート更新後に実行）
        setTimeout(() => {
          const iframe = document.querySelector("iframe");
          if (iframe && iframe.contentWindow) {
            const targetSelector = `.edit_concept_description_${index}_image`;

            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.IMAGE_UPDATE,
                target: targetSelector,
                contentKey,
                fieldName: "image",
                value: image,
                index,
                allData: { concept_description: newConceptDescription },
              },
              process.env.REACT_APP_ORIGIN
            );

            // スクロール命令も送信
            iframe.contentWindow.postMessage(
              {
                type: MESSAGE_TYPES.SCROLL_TO,
                target: targetSelector,
                contentKey,
              },
              process.env.REACT_APP_ORIGIN
            );
          }
        }, 0);

        return newConceptDescription;
      });
      setHasChanges(true);
    },
    [contentKey]
  );

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            {/* パンくず */}
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />

            <div className={`message ${message ? "success" : ""}`}>
              <p>{message}</p>
            </div>

            {/* 編集フォーム */}
            <div className="form">
              {/* コンセプトメッセージ */}
              <section>
                <div className="area1">
                  <textarea
                    className="input2"
                    name="concept_message"
                    value={pageContent?.concept_message || ""}
                    onChange={handleInputChange}
                    onFocus={handleInputChange}
                    data-label="コンセプトメッセージ"
                    data-required="true"
                    rows={10}
                    placeholder="改行区切りでメッセージを入力してください"
                  />
                </div>

                <div className="img-form">
                  {/* PC用動画リスト（ランダム表示） */}
                  <div className="h5-contents">
                    <h5>PC用動画リスト（ランダム表示）</h5>
                    <MediaSelector
                      label="PC用動画リスト"
                      maxSize="50MB"
                      type="video"
                      fieldName="pc_videos"
                      selectedMedia={pcVideos}
                      onSelect={handleVideosChange("pc_videos", setPcVideos)}
                      mode="multiple"
                    />
                  </div>

                  {/* スマホ用動画リスト（ランダム表示） */}
                  <div className="h5-contents">
                    <h5>スマホ用動画リスト（ランダム表示）</h5>
                    <MediaSelector
                      label="スマホ用動画リスト"
                      maxSize="50MB"
                      type="video"
                      fieldName="mobile_videos"
                      selectedMedia={mobileVideos}
                      onSelect={handleVideosChange(
                        "mobile_videos",
                        setMobileVideos
                      )}
                      mode="multiple"
                    />
                  </div>

                </div>
              </section>

              <section>
                <div className="area2">
                  {conceptDescription &&
                    conceptDescription.map((item, index) => (
                      <div className="item" key={index}>
                        <div className="ctrl">
                          <p>{index + 1}</p>
                          <div className="icon-wrap">
                            <i
                              className="icon delete"
                              onClick={() => handleDelete(index, item)}
                            ></i>
                            <i
                              className="icon add"
                              onClick={() => handleAdd(index)}
                            ></i>
                          </div>
                        </div>
                        <div className="forms">
                          {/* コンセプト タイトル */}
                          <input
                            type="text"
                            className="input1"
                            name={`concept_description${index}_title`}
                            value={item.title || ""}
                            placeholder="タイトルを入力"
                            onChange={(e) => handleConceptInputChange(e, index)}
                            onFocus={(e) => handleConceptInputChange(e, index)} // フォーカス時にも同じハンドラーを呼び出す
                            data-label="コンセプト タイトル"
                            data-required="true"
                            data-index={index}
                          />

                          {/* コンセプト 本文 */}
                          <textarea
                            className="input2"
                            name={`concept_description${index}_content`}
                            value={item.content || ""}
                            placeholder="内容を入力"
                            onChange={(e) => handleConceptInputChange(e, index)}
                            onFocus={(e) => handleConceptInputChange(e, index)} // フォーカス時にも同じハンドラーを呼び出す
                            data-label="コンセプト 本文"
                            data-required="true"
                            data-index={index}
                            rows={5}
                          />

                          {/* コンセプト 画像 */}
                          <div className="input3 img-form">
                            <MediaSelector
                              label={`コンセプト画像 ${index + 1}`}
                              aspectRatio="16:9"
                              type="image"
                              fieldName={`concept_description_${index}_image`}
                              selectedMedia={item.image}
                              onSelect={(image) =>
                                handleConceptImageChange(index, image)
                              }
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </section>
            </div>

            {/* 送信ボタン */}
            <div className="submit-area">
              <button type="reset" onClick={handleReset}>
                Reset
              </button>
              <button type="submit" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {/* メディア / オーバーレイ */}
            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {/* ローディング */}
      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div> <p>{loadingText}...</p>
          </div>
        </div>
      )}

      {/* プレビュー */}
      <Iframe path={"/about"} data={pageContent} contentKey={contentKey} />
    </>
  );
}

// MediaProviderでラップしたコンポーネントをエクスポート
export default function AboutEditPage() {
  return (
    <MediaProvider>
      <AboutEditPageContent />
    </MediaProvider>
  );
}
